import { wrapApiCall } from '@/lib/axios'
import type {
  PaginatedResult,
  TrainerClassTransactionRow,
  TrainerConductOverview,
  TrainerSessionTarget,
  TrainerTrainingTransactionRow,
} from '@/features/trainer-performance/types/trainer-performance'
import type { ReportLang } from '@/utils/helper/reportLang'
import type { AxiosInstance } from 'axios'
import {
  mapConductOverviewToUi,
  mapFeeTransactionToClassRow,
  mapFeeTransactionToTrainingRow,
  mapPaginatedFeeTransactions,
  mapTrainerPerformanceToSessionTarget,
} from './mappers'
import type {
  PaginatedResultDto,
  TrainerConductFeeConductType,
  TrainerConductFeeTransactionEntity,
  TrainerConductOverviewEntity,
  TrainerConductSummaryRowEntity,
  TrainerPerformanceEntity,
  UpdateTrainerTargetDto,
} from './model'

export type GetConductFeeTransactionsParams = {
  page: number
  pageSize: number
  query?: string
  conductType: TrainerConductFeeConductType
}

export type DownloadConductFeeTransactionsExcelParams = Pick<
  GetConductFeeTransactionsParams,
  'conductType' | 'query'
>

export const TrainerPerformanceService = (client: AxiosInstance) => {
  return {
    async findByTrainerId(trainerId: number): Promise<TrainerSessionTarget> {
      const { data } = await wrapApiCall(() =>
        client.get<TrainerPerformanceEntity>(`/trainer-performances/${trainerId}`)
      )
      return mapTrainerPerformanceToSessionTarget(data)
    },

    async getConductOverview(
      trainerId: number,
      params: { month: number; year: number }
    ): Promise<TrainerConductOverview> {
      const { data } = await wrapApiCall(() =>
        client.get<TrainerConductOverviewEntity>(
          `/trainer-performances/${trainerId}/conduct-overview`,
          { params: { month: params.month, year: params.year } }
        )
      )
      return mapConductOverviewToUi(data)
    },

    async getConductSummary(params: {
      startDate?: string
      endDate?: string
      query?: string
    }): Promise<TrainerConductSummaryRowEntity[]> {
      const queryParams: Record<string, string> = {}
      if (params.startDate) queryParams.startDate = params.startDate
      if (params.endDate) queryParams.endDate = params.endDate
      if (params.query) queryParams.query = params.query

      const { data } = await wrapApiCall(() =>
        client.get<TrainerConductSummaryRowEntity[]>('/trainer-performances/conduct-summary', {
          params: queryParams,
        })
      )
      return data ?? []
    },

    /**
     * The Account & Finance conduct report as one Excel workbook — the
     * per-trainer summary plus the class, attendee and private-session detail
     * behind it, cut on the same contract-type / search filters the report page
     * carries.
     */
    async downloadConductReportWorkbook(params: {
      startDate: string
      endDate: string
      contractType?: 'ALL' | 'PERMANENT' | 'FREELANCE'
      query?: string
      lang: ReportLang
    }): Promise<Blob> {
      const { contractType, query, ...rest } = params
      const { data } = await wrapApiCall(() =>
        client.get<Blob>('/trainer-performances/conduct-report/workbook', {
          params: {
            ...rest,
            ...(contractType ? { contractType } : {}),
            ...(query ? { query } : {}),
          },
          responseType: 'blob',
          headers: {
            Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        })
      )
      return data
    },

    async getConductFeeTransactions(
      trainerId: number,
      opt: GetConductFeeTransactionsParams
    ): Promise<
      PaginatedResult<TrainerTrainingTransactionRow> | PaginatedResult<TrainerClassTransactionRow>
    > {
      const params: Record<string, string | number> = {
        page: opt.page,
        pageSize: opt.pageSize,
        conductType: opt.conductType,
      }
      if (opt.query !== undefined && opt.query !== '') {
        params.query = opt.query
      }

      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResultDto<TrainerConductFeeTransactionEntity>>(
          `/trainer-performances/${trainerId}/conduct-fee-transactions`,
          { params }
        )
      )

      if (opt.conductType === 'TRAINING') {
        return mapPaginatedFeeTransactions(data, mapFeeTransactionToTrainingRow)
      }
      return mapPaginatedFeeTransactions(data, mapFeeTransactionToClassRow)
    },

    async downloadConductFeeTransactionsExcel(
      trainerId: number,
      opt: DownloadConductFeeTransactionsExcelParams
    ): Promise<Blob> {
      const params: Record<string, string> = {
        conductType: opt.conductType,
      }
      if (opt.query !== undefined && opt.query !== '') {
        params.query = opt.query
      }

      const { data } = await wrapApiCall(() =>
        client.get<Blob>(
          `/trainer-performances/${trainerId}/conduct-fee-transactions/report/excel`,
          {
            params,
            responseType: 'blob',
            headers: {
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
          }
        )
      )

      return data
    },

    async updateTrainerTarget(
      trainerId: number,
      body: UpdateTrainerTargetDto
    ): Promise<TrainerSessionTarget> {
      const { data } = await wrapApiCall(() =>
        client.patch<TrainerPerformanceEntity>(`/trainer-performances/${trainerId}/target`, body)
      )
      return mapTrainerPerformanceToSessionTarget(data)
    },
  }
}

export type {
  TrainerConductClassDetailRowEntity,
  TrainerConductDetailEntity,
  TrainerConductSummaryRowEntity,
  TrainerConductTrainingDetailRowEntity,
  UpdateTrainerTargetDto,
} from './model'
